import Stack from "@mui/material/Stack";
import LoadingButton from "@mui/lab/LoadingButton";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import {
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  Switch,
  Typography,
} from "@mui/material";
import { useState } from "react";
import JumboAppTextField from "@jumbo/components/JumboAppTextField";

// LR (Lorry Receipt) copy: JPG / JPEG / PNG / PDF, one file per dispatch.
const LR_COPY_ACCEPT = ".jpg,.jpeg,.png,.pdf";
const LR_COPY_TYPES = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
const LR_COPY_MAX_BYTES = 5 * 1024 * 1024;

const ChangeStatus = ({ value, statusOf, onClose, handleSubmit }) => {
  // Files already attached to this invoice for the status being changed. The
  // list API returns both arrays, so nothing extra is fetched here.
  const existingFiles =
    (statusOf === "payment"
      ? value?.paymentStatusImage
      : value?.dispatchStatusImage) || [];
  // The LR copy belongs to the dispatch, so it is only offered on the Change
  // Dispatch Status popup - the payment popup is left exactly as it was.
  const isDispatch = statusOf !== "payment";
  const existingLrCopy = value?.lrCopy?.fileName ? value.lrCopy : null;
  const [lrCopyName, setLrCopyName] = useState("");
  const [lrCopyError, setLrCopyError] = useState("");
  const [checkedForPayment, setCheckedForPayment] = useState(
    value?.paymentStatus === "paid" ? true : false
  );
  const [checkedForDispatch, setCheckedForDispatch] = useState(
    value?.statusOf !== "payment" && value?.dispatchStatus === "dispatched"
      ? true
      : false
  );
  const [switchLabelForPayment, setSwitchLabelForPayment] = useState(
    value?.paymentStatus?.[0]?.toUpperCase() + value?.paymentStatus?.slice(1)
  );
  const [switchLabelForDispatch, setSwitchLabelForDispatch] = useState(
    value.statusOf !== "payment" &&
      value?.dispatchStatus?.[0]?.toUpperCase() +
        value?.dispatchStatus?.slice(1)
  );

  const validationSchema = Yup.object({
    // status: Yup.string().required("Please enter vendor"),
    // markaNumber: Yup.string().required("Please enter marka number"),
    // file: Yup.mixed().required("A file is required"),
  });

  return (
    <>
      <Formik
        initialValues={{
          status: "",
          files: null,
          lrCopy: null,
          markaNumber: value?.markaNumber,
        }}
        validationSchema={validationSchema}
        onSubmit={async (data, { setSubmitting }) => {
          data.paymentStatus = checkedForPayment;
          data.dispatchStatus = checkedForDispatch;
          data.invoiceId = value._id;
          setSubmitting(true);
          setTimeout(() => {
            handleSubmit(data);
            setSubmitting(false);
          }, 1500);
        }}
      >
        {({ isSubmitting, setFieldValue }) => (
          <Form>
            <Grid container spacing={2} mb={4}>
              <Grid item sm={12} xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={checkedForPayment}
                      onChange={() => {
                        setCheckedForPayment((prev) => {
                          const newState = !prev;
                          setSwitchLabelForPayment(
                            newState ? "Paid" : "Unpaid"
                          );
                          return newState;
                        });
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                    />
                  }
                  label={switchLabelForPayment}
                />
              </Grid>
              {statusOf !== "payment" && (
                <Grid item sm={12} xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={checkedForDispatch}
                        onChange={() => {
                          setCheckedForDispatch((prev) => {
                            const newState = !prev;
                            if (statusOf !== "payment") {
                              setSwitchLabelForDispatch(
                                newState ? "Dispatched" : "Pending"
                              );
                            }
                            return newState;
                          });
                        }}
                        inputProps={{ "aria-label": "controlled" }}
                      />
                    }
                    label={switchLabelForDispatch}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <JumboAppTextField
                  size="small"
                  fullWidth
                  name="markaNumber"
                  label="Marka Number"
                />
              </Grid>
              <Grid item sm={12} xs={12}>
                <input
                  type="file"
                  name="files"
                  multiple
                  accept="application/pdf,image/*"
                  onChange={(event) => {
                    setFieldValue(
                      "files",
                      Array.from(event.currentTarget.files)
                    );
                  }}
                />
              </Grid>
              {isDispatch && (
                <Grid item sm={12} xs={12}>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    LR Copy
                  </Typography>
                  {existingLrCopy && (
                    <Typography variant="body2" mb={0.5} noWrap>
                      Current:{" "}
                      <Link
                        href={`${process.env.REACT_APP_IMAGE_BASE_URL}invoice-images/${existingLrCopy.fileName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {existingLrCopy.originalName || existingLrCopy.fileName}
                      </Link>
                    </Typography>
                  )}
                  <input
                    type="file"
                    name="lrCopy"
                    accept={LR_COPY_ACCEPT}
                    onChange={(event) => {
                      const [file] = Array.from(event.currentTarget.files || []);
                      if (!file) {
                        setFieldValue("lrCopy", null);
                        setLrCopyName("");
                        setLrCopyError("");
                        return;
                      }
                      // Same rules the API enforces, checked here too so the
                      // mistake is caught before the upload round-trip.
                      if (!LR_COPY_TYPES.includes(file.type)) {
                        setFieldValue("lrCopy", null);
                        setLrCopyName("");
                        setLrCopyError(
                          "LR Copy must be a JPG, JPEG, PNG or PDF file."
                        );
                        event.currentTarget.value = "";
                        return;
                      }
                      if (file.size > LR_COPY_MAX_BYTES) {
                        setFieldValue("lrCopy", null);
                        setLrCopyName("");
                        setLrCopyError("LR Copy must be 5MB or smaller.");
                        event.currentTarget.value = "";
                        return;
                      }
                      setFieldValue("lrCopy", file);
                      setLrCopyName(file.name);
                      setLrCopyError("");
                    }}
                  />
                  {lrCopyName && (
                    <Typography variant="body2" color="text.secondary" noWrap>
                      Selected: {lrCopyName}
                    </Typography>
                  )}
                  {lrCopyError && (
                    <Typography variant="body2" color="error">
                      {lrCopyError}
                    </Typography>
                  )}
                </Grid>
              )}
              {existingFiles.length > 0 && (
                <Grid item sm={12} xs={12}>
                  <Typography variant="body2" color="text.secondary" mb={0.5}>
                    Attached files
                  </Typography>
                  {existingFiles.map((file) => (
                    <Typography variant="body2" key={file} noWrap>
                      <Link
                        href={`${process.env.REACT_APP_IMAGE_BASE_URL}invoice-images/${file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {file}
                      </Link>
                    </Typography>
                  ))}
                </Grid>
              )}
            </Grid>

            <Stack
              spacing={{ xs: 1, sm: 2 }}
              direction="row"
              justifyContent="center"
            >
              <LoadingButton
                variant="outlined"
                color="secondary"
                onClick={onClose}
                size="small"
              >
                Cancel
              </LoadingButton>
              <LoadingButton
                type="submit"
                variant="contained"
                size="small"
                sx={{ mb: 3 }}
                loading={isSubmitting}
                loadingIndicator={<CircularProgress size={20} />}
              >
                Save
              </LoadingButton>
            </Stack>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ChangeStatus;
